/**
 * ControlTowerPage — J3-A (W5 expansion: hierarchy + saved views + timeline).
 *
 * Prioritised queue view for the Control Tower work-item list with three
 * J7-W5 additions:
 *
 *  - Hierarchical table: root rows expand to reveal sub-items via the
 *    new /api/v1/control-tower/work-items?parentId= filter. Parent rows
 *    show a child-count badge sourced from /tree.
 *
 *  - Saved Views: Views dropdown lists own + org-shared views; "Save
 *    current filter…" creates one; per-view Edit/Delete actions.
 *
 *  - Timeline view: Table | Timeline toggle in the header. Both views
 *    consume the same filter chips and saved-view selection.
 *
 *  - Board view: Kanban board with drag-and-drop status transitions.
 *    Default view for new users (no localStorage value). Drag respects
 *    the state machine and shows WIP indicators per column (LEAN-01).
 *
 * Compliance: LEAN-01 (WIP banner), LEAN-07 (items start in backlog),
 *             CT-02 (state machine enforced in drawer / transitions),
 *             CT-04 (Strategic + Operational — sub-items strengthen Operational),
 *             GOV-05 (audit on saved-view CRUD).
 * PRD §12.3.6 + §12.3.10 task 6c.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Radio, Plus, Filter, ChevronRight, ChevronDown, BookmarkPlus, Trash2, Pencil, BarChart3, Table as TableIcon, LayoutGrid } from 'lucide-react';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select/Select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/localeFormatters';
import {
  useRorosWorkItems,
  useRorosSavedViews,
  useRorosSavedViewDelete,
  useRorosWorkItemTree,
  useRorosWorkItemRollup,
  useRorosWorkItem,
} from '@/hooks/roros/control-tower';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosActors } from '@/hooks/roros/actors';
import type { RorosWorkItemDTO } from '@/types/roros/control-tower';
import type { RorosSavedViewDTO } from '@/types/roros/saved-views';
import { WipStatusBanner } from './WipStatusBanner';
import { WorkItemFormDialog } from './WorkItemFormDialog';
import { WorkItemDrawer } from './WorkItemDrawer';
import { SavedViewModal } from './SavedViewModal';
import { WorkItemTimeline } from './WorkItemTimeline';
import { WorkItemBoard, type BoardSortKey } from './WorkItemBoard';
// M-Rhythm-2: Today's meetings strip (meetings are NOT work items; no WIP impact)
import { TodaysMeetingsStrip } from '@/components/roros/meetings/TodaysMeetingsStrip';

// ─── Status badge variants ─────────────────────────────────────────────────

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'default' | 'primary' {
  switch (status) {
    case 'released':           return 'success';
    case 'in_progress':        return 'primary';
    case 'quality_confirmation': return 'warning';
    case 'blocked':
    case 'escalated':          return 'error';
    case 'done':               return 'default';
    default:                   return 'default';
  }
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    backlog:               t('Backlog'),
    ready:                 t('Ready'),
    in_progress:           t('In Progress'),
    done:                  t('Done'),
    quality_confirmation:  t('QC'),
    released:              t('Released'),
    blocked:               t('Blocked'),
    escalated:             t('Escalated'),
  };
  return map[status] ?? status;
}

function priorityLabel(p: number): string {
  const map: Record<number, string> = {
    1: '🔴 ' + t('Critical'),
    2: '🟠 ' + t('High'),
    3: '🟡 ' + t('Medium'),
    4: '🟢 ' + t('Low'),
    5: '⚪ ' + t('Lowest'),
  };
  return map[p] ?? String(p);
}

// ─── Filter options ────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'all',                  label: t('All statuses') },
  { value: 'backlog',              label: t('Backlog') },
  { value: 'ready',                label: t('Ready') },
  { value: 'in_progress',          label: t('In Progress') },
  { value: 'done',                 label: t('Done') },
  { value: 'quality_confirmation', label: t('QC') },
  { value: 'released',             label: t('Released') },
  { value: 'blocked',              label: t('Blocked') },
  { value: 'escalated',            label: t('Escalated') },
];

type ViewMode = 'table' | 'timeline' | 'board';

const VIEW_MODE_STORAGE_KEY = 'roros.controlTower.viewMode';
const DEFAULT_VIEW_MODE: ViewMode = 'board';

// Fix 7: Board sort
const BOARD_SORT_STORAGE_KEY = 'roros.controlTower.boardSort';
const DEFAULT_BOARD_SORT: BoardSortKey = 'default';

const BOARD_SORT_OPTIONS = [
  { value: 'default',  label: t('Due date · Priority') },
  { value: 'priority', label: t('Priority (High → Low)') },
  { value: 'due-asc',  label: t('Due date (soonest first)') },
  { value: 'title',    label: t('Title (A → Z)') },
  { value: 'updated',  label: t('Recently updated') },
] as const;

// ─── Sub-items expansion sub-component (J7-W5 task 6c-1) ───────────────────

function SubItemsExpansion({
  parent,
  domainName,
  actorName,
  onRowClick,
}: {
  parent: RorosWorkItemDTO;
  domainName: (id: string) => string;
  actorName: (id: string | null) => string;
  onRowClick: (item: RorosWorkItemDTO) => void;
}) {
  const tree = useRorosWorkItemTree(parent.id);
  const children = (tree.data ?? []).filter((c) => c.id !== parent.id);

  if (tree.isLoading) {
    return (
      <tr>
        <td colSpan={9} className="px-3 py-2 text-xs text-muted-foreground">
          {t('Loading sub-items…')}
        </td>
      </tr>
    );
  }
  if (children.length === 0) {
    return (
      <tr>
        <td colSpan={9} className="px-3 py-2 pl-12 text-xs text-muted-foreground">
          {t('No sub-items yet.')}
        </td>
      </tr>
    );
  }
  return (
    <>
      {children.map((c) => (
        <tr
          key={c.id}
          className="border-b border-border last:border-0 bg-muted/10 hover:bg-muted/30 cursor-pointer transition-colors"
          onClick={() => onRowClick(c)}
          data-testid={`subitem-row-${c.id}`}
        >
          <td className="px-3 py-2 pl-10">
            <span className="text-foreground">{c.title}</span>
          </td>
          <td className="px-3 py-2 text-muted-foreground">{actorName(c.ownerActorId)}</td>
          <td className="px-3 py-2 text-muted-foreground">{domainName(c.domainId)}</td>
          <td className="px-3 py-2">
            <Badge variant={statusVariant(c.status)}>{statusLabel(c.status)}</Badge>
          </td>
          <td className="px-3 py-2 text-muted-foreground text-xs">{priorityLabel(c.priority)}</td>
          <td className="px-3 py-2 text-muted-foreground text-xs">—</td>
          <td className="px-3 py-2 text-muted-foreground text-xs">{c.dueDate ? formatDate(c.dueDate) : '—'}</td>
          <td className="px-3 py-2 text-muted-foreground text-xs">
            {c.definitionOfDone ? <span className="text-green-600">✓</span> : <span className="text-muted-foreground/50">—</span>}
          </td>
        </tr>
      ))}
    </>
  );
}

function RollupBadge({ parentId }: { parentId: string }) {
  const rollup = useRorosWorkItemRollup(parentId);
  if (!rollup.data || rollup.data.total === 0) return null;
  return (
    <span
      className="ml-2 text-xs bg-muted text-muted-foreground rounded-full px-1.5 py-0.5"
      data-testid={`rollup-badge-${parentId}`}
    >
      {`${rollup.data.completed}/${rollup.data.total} ` + t('done')}
    </span>
  );
}

// ─── ControlTowerPage ──────────────────────────────────────────────────────

export function ControlTowerPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── Filters ───────────────────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDomain, setFilterDomain] = useState<string>('all');
  const [filterOwner,  setFilterOwner]  = useState<string>('all');

  // ─── UI state ──────────────────────────────────────────────────────────────
  const [isFormOpen,    setIsFormOpen]    = useState(false);
  const [selectedItem,  setSelectedItem]  = useState<RorosWorkItemDTO | null>(null);

  // ─── AC-hard-1: deep-link via ?item=<id> ──────────────────────────────────
  // When the page loads with a ?item= param (e.g. from a notification click),
  // fetch that work item and open its drawer automatically.
  const deepLinkItemId = searchParams.get('item') ?? '';
  const deepLinkQuery = useRorosWorkItem(deepLinkItemId);

  useEffect(() => {
    if (!deepLinkItemId || !deepLinkQuery.data || selectedItem?.id === deepLinkItemId) return;
    // Open the drawer for the deep-linked item
    setSelectedItem(deepLinkQuery.data);
    // Remove the ?item= param so the URL stays clean after the drawer opens.
    // Use replace so the browser back-button doesn't re-trigger the deep-link.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('item');
      return next;
    }, { replace: true });
  }, [deepLinkItemId, deepLinkQuery.data, selectedItem?.id, setSearchParams]);
  const [expanded,      setExpanded]      = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (stored === 'table' || stored === 'timeline' || stored === 'board') return stored;
    } catch {
      // localStorage unavailable (e.g. SSR or private browsing)
    }
    return DEFAULT_VIEW_MODE;
  });
  const [savedViewModalOpen, setSavedViewModalOpen] = useState(false);
  const [editingView,   setEditingView]   = useState<RorosSavedViewDTO | null>(null);
  const [deletingViewId, setDeletingViewId] = useState<string | null>(null);
  const [activeViewId,  setActiveViewId]  = useState<string>('all');

  // Fix 7: Board column sort — persisted in localStorage
  const [boardSort, setBoardSort] = useState<BoardSortKey>(() => {
    try {
      const stored = localStorage.getItem(BOARD_SORT_STORAGE_KEY);
      if (stored === 'default' || stored === 'priority' || stored === 'due-asc' || stored === 'title' || stored === 'updated') {
        return stored as BoardSortKey;
      }
    } catch {
      // localStorage unavailable
    }
    return DEFAULT_BOARD_SORT;
  });

  // ─── Data ──────────────────────────────────────────────────────────────────
  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filterStatus !== 'all') p.status = filterStatus;
    if (filterDomain !== 'all') p.domainId = filterDomain;
    if (filterOwner  !== 'all') p.ownerActorId = filterOwner;
    // Hierarchy: root view shows only items WHERE parent_work_item_id IS NULL.
    // (Children are loaded inline via SubItemsExpansion → /tree.)
    p.includeRoots = 'true';
    return p;
  }, [filterStatus, filterDomain, filterOwner]);

  const workItemsQuery = useRorosWorkItems(queryParams);
  const domainsQuery   = useRorosDomains();
  const actorsQuery    = useRorosActors();
  const savedViewsQuery = useRorosSavedViews('control_tower');
  const deleteView = useRorosSavedViewDelete('control_tower');

  const domainOptions = useMemo(
    () => [
      { value: 'all', label: t('All domains') },
      ...(domainsQuery.data?.filter(d => d.isActive).map(d => ({ value: d.id, label: d.name })) ?? []),
    ],
    [domainsQuery.data],
  );

  const ownerOptions = useMemo(
    () => [
      { value: 'all', label: t('All owners') },
      ...(actorsQuery.data?.map(a => ({ value: a.id, label: a.name })) ?? []),
    ],
    [actorsQuery.data],
  );

  const viewOptions = useMemo(
    () => [
      { value: 'all', label: t('All') },
      ...((savedViewsQuery.data ?? []).map((v) => ({ value: v.id, label: v.name }))),
    ],
    [savedViewsQuery.data],
  );

  // Apply a saved view: copy its stored filter onto the current chips state.
  const applyView = useCallback((id: string) => {
    setActiveViewId(id);
    if (id === 'all') return;
    const view = (savedViewsQuery.data ?? []).find((v) => v.id === id);
    if (!view) return;
    const f = view.filter ?? {};
    setFilterStatus((f as any).status ?? 'all');
    setFilterDomain((f as any).domainId ?? 'all');
    setFilterOwner((f as any).ownerActorId ?? 'all');
  }, [savedViewsQuery.data]);

  const handleRowClick = useCallback((item: RorosWorkItemDTO) => {
    setSelectedItem(item);
  }, []);

  const handleFormSuccess = useCallback(() => {
    setIsFormOpen(false);
  }, []);

  const handleToggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ─── Domain name lookup helper ────────────────────────────────────────────

  const domainName = useCallback((domainId: string) => {
    return domainsQuery.data?.find(d => d.id === domainId)?.name ?? domainId.slice(0, 8);
  }, [domainsQuery.data]);

  const actorName = useCallback((actorId: string | null) => {
    if (!actorId) return '—';
    return actorsQuery.data?.find(a => a.id === actorId)?.name ?? actorId.slice(0, 8);
  }, [actorsQuery.data]);

  const currentFilter: Record<string, string> = {
    status: filterStatus,
    domainId: filterDomain,
    ownerActorId: filterOwner,
  };

  // Persist view mode to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // Ignore storage errors
    }
  }, [viewMode]);

  // Fix 7: Persist board sort to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(BOARD_SORT_STORAGE_KEY, boardSort);
    } catch {
      // Ignore storage errors
    }
  }, [boardSort]);

  // The Parent picker on the create dialog needs the list of root work items.
  const rootCandidates = useMemo(
    () => (workItemsQuery.data ?? []).map((w: RorosWorkItemDTO) => ({ id: w.id, title: w.title })),
    [workItemsQuery.data],
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <RorosPage
      title={t('Control Tower')}
      icon={<Radio className="h-6 w-6" />}
      breadcrumbs={[{ label: t('ROROS'), path: '/roros' }]}
      fullHeight
      actions={
        <div className="flex items-center gap-2">
          {/* View mode toggle (Table | Board | Timeline) */}
          <Tabs
            value={viewMode}
            onValueChange={(v) => setViewMode(v as ViewMode)}
            className="w-auto"
          >
            <TabsList className="h-auto p-0.5">
              <TabsTrigger
                value="table"
                className="px-2 py-1 text-xs gap-1 h-auto"
                aria-label={t('Switch to table view')}
                data-testid="view-mode-table"
              >
                <TableIcon className="h-3.5 w-3.5" /> {t('Table')}
              </TabsTrigger>
              <TabsTrigger
                value="board"
                className="px-2 py-1 text-xs gap-1 h-auto"
                aria-label={t('Switch to board view')}
                data-testid="view-mode-board"
              >
                <LayoutGrid className="h-3.5 w-3.5" /> {t('Board')}
              </TabsTrigger>
              <TabsTrigger
                value="timeline"
                className="px-2 py-1 text-xs gap-1 h-auto"
                aria-label={t('Switch to timeline view')}
                data-testid="view-mode-timeline"
              >
                <BarChart3 className="h-3.5 w-3.5" /> {t('Timeline')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={() => setIsFormOpen(true)} size="sm" data-testid="new-work-item-cta">
            <Plus className="h-4 w-4 mr-1" />
            {t('New Work Item')}
          </Button>
        </div>
      }
    >
      {/* Flex-column layout: fixed header rows + flex-1 content area.
          Required for the board's h-full to propagate correctly in fullHeight mode. */}
      <div className="flex flex-col h-full">
        {/* Today's meetings strip — M-Rhythm-2.
            Meetings are NOT work items; this strip has zero WIP impact.
            Visual divider (border-b) separates meetings from work items below. */}
        <div className="pb-3 mb-3 border-b border-border flex-shrink-0">
          <TodaysMeetingsStrip />
        </div>

        {/* WIP Status Banner — Fix 3: hidden in board view; column-level WIP indicator covers this signal */}
        {viewMode !== 'board' && (
          <div className="mb-4 flex-shrink-0">
            <WipStatusBanner />
          </div>
        )}

        {/* Filter chips + Views dropdown — Fix 4: single row (flex-nowrap + overflow-x-auto) */}
        <div className="flex flex-nowrap items-center gap-2 mb-4 overflow-x-auto pb-1 flex-shrink-0">
        <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <Select
          options={STATUS_OPTIONS}
          value={filterStatus}
          onValueChange={(v) => { setFilterStatus(v); setActiveViewId('all'); }}
          className="min-w-[150px]"
          aria-label={t('Filter by status')}
        />
        <Select
          options={domainOptions}
          value={filterDomain}
          onValueChange={(v) => { setFilterDomain(v); setActiveViewId('all'); }}
          className="min-w-[150px]"
          aria-label={t('Filter by domain')}
        />
        <Select
          options={ownerOptions}
          value={filterOwner}
          onValueChange={(v) => { setFilterOwner(v); setActiveViewId('all'); }}
          className="min-w-[160px]"
          aria-label={t('Filter by owner')}
        />

        {/* Fix 7: Board sort dropdown — only shown in board view */}
        {viewMode === 'board' && (
          <Select
            options={[...BOARD_SORT_OPTIONS]}
            value={boardSort}
            onValueChange={(v) => setBoardSort(v as BoardSortKey)}
            className="min-w-[180px] flex-shrink-0"
            aria-label={t('Sort cards')}
            data-testid="board-sort-select"
          />
        )}

        <div className="flex items-center gap-1 ml-2 border-l border-border pl-2 flex-shrink-0" data-testid="views-dropdown">
          <Select
            options={viewOptions}
            value={activeViewId}
            onValueChange={applyView}
            className="min-w-[140px]"
            aria-label={t('Views')}
          />
          {/* Fix 4: icon-only Save button to save horizontal space */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setEditingView(null); setSavedViewModalOpen(true); }}
            aria-label={t('Save current filter…')}
            data-testid="save-view-cta"
          >
            <BookmarkPlus className="h-3.5 w-3.5" />
          </Button>
          {activeViewId !== 'all' && (() => {
            const view = (savedViewsQuery.data ?? []).find((v) => v.id === activeViewId);
            if (!view) return null;
            return (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEditingView(view); setSavedViewModalOpen(true); }}
                  aria-label={t('Edit view')}
                  data-testid="edit-view-cta"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeletingViewId(view.id)}
                  aria-label={t('Delete view')}
                  data-testid="delete-view-cta"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            );
          })()}
        </div>

        {(filterStatus !== 'all' || filterDomain !== 'all' || filterOwner !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            className="flex-shrink-0"
            onClick={() => {
              setFilterStatus('all');
              setFilterDomain('all');
              setFilterOwner('all');
              setActiveViewId('all');
            }}
          >
            {t('Clear filters')}
          </Button>
        )}
        </div>

        {/* Content area: board fills h-full; table/timeline get their own overflow-y-auto scroll */}
        <div className={viewMode === 'board' ? 'flex-1 min-h-0 overflow-hidden' : 'flex-1 min-h-0 overflow-y-auto'}>
          <QueryPage
            query={workItemsQuery}
            className={viewMode === 'board' ? 'h-full' : undefined}
            emptyMessage={viewMode === 'board' ? t('No work items yet — drag and drop when you have some') : viewMode === 'timeline' ? t('No items match the current filter.') : t('No work items yet')}
            emptyAction={
              viewMode !== 'board' ? (
                <Button onClick={() => setIsFormOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('Create your first work item')}
                </Button>
              ) : undefined
            }
            emptyIcon={<Radio className="h-8 w-8 text-muted-foreground" />}
            loadingText={t('Loading work items...')}
          >
            {(items) => (
              viewMode === 'board' ? (
                // E.1 Phase: -mx wrapper removed; fullHeight + h-full chain handles edge-to-edge and height
                <WorkItemBoard
                  items={items as RorosWorkItemDTO[]}
                  domains={domainsQuery.data ?? []}
                  actors={actorsQuery.data ?? []}
                  onOpenDrawer={handleRowClick}
                  activeStatusFilter={filterStatus}
                  boardSort={boardSort}
                />
              ) : viewMode === 'timeline' ? (
                <WorkItemTimeline
                  items={items as RorosWorkItemDTO[]}
                  domains={domainsQuery.data ?? []}
                  onSelect={handleRowClick}
                />
              ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('Title')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('Owner')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('Domain')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('Status')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('Priority')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('Linked Outcome')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('Due')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">{t('DoD')}</th>
                </tr>
              </thead>
              <tbody>
                {(items as RorosWorkItemDTO[]).map((item) => (
                  <React.Fragment key={item.id}>
                    <tr
                      className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(item)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(item);
                        }
                      }}
                      aria-label={`${t('Open work item')}: ${item.title}`}
                      data-testid={`work-item-row-${item.id}`}
                    >
                      <td className="px-3 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleToggleExpand(item.id); }}
                          className="inline-flex items-center text-muted-foreground hover:text-foreground mr-2 h-6 w-6 p-0"
                          aria-label={expanded.has(item.id) ? t('Collapse') : t('Expand')}
                          data-testid={`expand-${item.id}`}
                        >
                          {expanded.has(item.id) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </Button>
                        <span className="font-medium text-foreground">{item.title}</span>
                        {item.type !== 'task' && (
                          <span className="ml-2 text-xs text-muted-foreground">({item.type})</span>
                        )}
                        <RollupBadge parentId={item.id} />
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{actorName(item.ownerActorId)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{domainName(item.domainId)}</td>
                      <td className="px-3 py-2">
                        <Badge variant={statusVariant(item.status)}>
                          {statusLabel(item.status)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {priorityLabel(item.priority)}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs max-w-[140px]">
                        {item.outcomeTitle ? (
                          <span className="truncate block" title={item.outcomeTitle}>
                            {item.outcomeTitle.length > 20
                              ? item.outcomeTitle.slice(0, 20) + '…'
                              : item.outcomeTitle}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {item.dueDate ? formatDate(item.dueDate) : '—'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {item.definitionOfDone ? (
                          <span className="text-green-600" title={item.definitionOfDone}>✓</span>
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </td>
                    </tr>
                    {expanded.has(item.id) && (
                      <SubItemsExpansion
                        parent={item}
                        domainName={domainName}
                        actorName={actorName}
                        onRowClick={handleRowClick}
                      />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            </div>
            )
          )}
          </QueryPage>
        </div>{/* end content area */}
      </div>{/* end flex-column layout */}

      {/* Create Work Item Dialog */}
      <WorkItemFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
        domains={domainsQuery.data?.filter(d => d.isActive) ?? []}
        actors={actorsQuery.data ?? []}
        parentCandidates={rootCandidates}
      />

      {/* Saved view modal */}
      <SavedViewModal
        isOpen={savedViewModalOpen}
        onClose={() => setSavedViewModalOpen(false)}
        view={editingView}
        currentFilter={currentFilter}
        onSaved={(saved) => setActiveViewId(saved.id)}
      />

      {/* Confirm delete saved view */}
      {deletingViewId && (
        <ConfirmDialog
          isOpen
          title={t('Delete this view?')}
          description={t('This will remove the saved view permanently.')}
          confirmText={t('Delete view')}
          variant="danger"
          onClose={() => setDeletingViewId(null)}
          onConfirm={async () => {
            await deleteView.mutateAsync(deletingViewId);
            setDeletingViewId(null);
            setActiveViewId('all');
          }}
        />
      )}

      {/* Work Item Detail Drawer */}
      {selectedItem && (
        <WorkItemDrawer
          workItemId={selectedItem.id}
          onClose={() => setSelectedItem(null)}
          domains={domainsQuery.data ?? []}
          actors={actorsQuery.data ?? []}
        />
      )}
    </RorosPage>
  );
}
